// Hook für POI-Sichtbarkeit. Ein Boolean pro Kategorie, persistiert in
// localStorage. Synchronisiert via usePreference mit dem Rest der App.
//
// Wave 14cf-fix15: alle 6 Kategorien (drinking_water + 5 admin-only) bekommen
// jetzt ihre eigenen Toggles. Vorher war nur drinking_water togglebar; cafe/
// pranzo/bike_shop/beach/viewpoint waren "always-on" (s. comment in fix10).
// User-Bug: "Café POI angelegt aber nicht im Toggle-Panel" — der User
// erwartet einen Toggle pro Kategorie, sonst weiß er nicht, was sichtbar ist.

import { useEffect, useState } from 'react';
import { useBoolPreference } from './usePreference';
import { poiOverrideBus, type PoiOverrideMode } from './poiOverrideBus';
import { OVERPASS_EXTRA_CATEGORIES, type PoiCategoryId } from './poi';

export const PREF_POI_DRINKING_WATER = 'adt_poi_drinking_water';
export const DEFAULT_POI_DRINKING_WATER = false;

export const PREF_POI_CAFE = 'adt_poi_cafe';
export const DEFAULT_POI_CAFE = true; // Cafés sind ein gewünschtes UX-Feature → default an

export const PREF_POI_PRANZO = 'adt_poi_pranzo';
export const DEFAULT_POI_PRANZO = true;

export const PREF_POI_BIKE_SHOP = 'adt_poi_bike_shop';
export const DEFAULT_POI_BIKE_SHOP = true;

export const PREF_POI_BEACH = 'adt_poi_beach';
export const DEFAULT_POI_BEACH = true;

export const PREF_POI_VIEWPOINT = 'adt_poi_viewpoint';
export const DEFAULT_POI_VIEWPOINT = true;

// Wave 14cn / Wave 14co: Battery-Swap-Stations als POI-Kategorie.
// Default FALSE — keine Stationen sichtbar bis der User aktiv durch
// die AllDayTurboPage geht und entweder "Got it" (VL) oder "Show demo"
// (Non-VL) anklickt. Beide Buttons setzen die Pref auf true. Damit
// haben Non-VL-User keinen unerwarteten Akku-Pillen-Spam auf der Karte,
// und VL-User haben einen klaren Onboarding-Flow.
export const PREF_POI_BATTERY_STATION = 'adt_poi_battery_station';
export const DEFAULT_POI_BATTERY_STATION = false;

export function usePoiDrinkingWater(): [boolean, (v: boolean) => void] {
  return useBoolPreference(PREF_POI_DRINKING_WATER, DEFAULT_POI_DRINKING_WATER);
}

export function usePoiCafe(): [boolean, (v: boolean) => void] {
  return useBoolPreference(PREF_POI_CAFE, DEFAULT_POI_CAFE);
}

export function usePoiPranzo(): [boolean, (v: boolean) => void] {
  return useBoolPreference(PREF_POI_PRANZO, DEFAULT_POI_PRANZO);
}

export function usePoiBikeShop(): [boolean, (v: boolean) => void] {
  return useBoolPreference(PREF_POI_BIKE_SHOP, DEFAULT_POI_BIKE_SHOP);
}

export function usePoiBeach(): [boolean, (v: boolean) => void] {
  return useBoolPreference(PREF_POI_BEACH, DEFAULT_POI_BEACH);
}

export function usePoiViewpoint(): [boolean, (v: boolean) => void] {
  return useBoolPreference(PREF_POI_VIEWPOINT, DEFAULT_POI_VIEWPOINT);
}

export function usePoiBatteryStation(): [boolean, (v: boolean) => void] {
  return useBoolPreference(
    PREF_POI_BATTERY_STATION,
    DEFAULT_POI_BATTERY_STATION,
  );
}

/**
 * Composite-Hook: liefert ein Map<category, boolean> + setter pro Kategorie.
 * Brauchen wir um in MapPage die customPoiSymbol-Features per Kategorie zu
 * filtern, ohne 6 Hook-Calls einzeln zu schreiben.
 */
export interface PoiVisibilityState {
  drinking_water: boolean;
  cafe: boolean;
  pranzo: boolean;
  bike_shop: boolean;
  beach: boolean;
  viewpoint: boolean;
}

/**
 * Wave 14ce: Session-Override. Wenn `poiOverrideBus` einen Mode hält,
 * liefert er ein synthetisches Visibility-State zurück das die persistente
 * User-Auswahl überschreibt — bis App-Reload (Bus ist in-memory only).
 * Trigger ist aktuell nur der SOS-Page "Bike defekt"-Button.
 */
function usePoiOverride(): PoiOverrideMode {
  const [mode, setMode] = useState<PoiOverrideMode>(() => poiOverrideBus.get());
  useEffect(() => {
    setMode(poiOverrideBus.get());
    return poiOverrideBus.subscribe(setMode);
  }, []);
  return mode;
}

export function usePoiVisibility(): PoiVisibilityState {
  const [drinking_water] = usePoiDrinkingWater();
  const [cafe] = usePoiCafe();
  const [pranzo] = usePoiPranzo();
  const [bike_shop] = usePoiBikeShop();
  const [beach] = usePoiBeach();
  const [viewpoint] = usePoiViewpoint();
  // Wave 14ce: Override hat Vorrang. Aktuell nur 'bike-only' definiert.
  const override = usePoiOverride();
  if (override === 'bike-only') {
    return {
      drinking_water: false,
      cafe: false,
      pranzo: false,
      bike_shop: true,
      beach: false,
      viewpoint: false,
    };
  }
  return { drinking_water, cafe, pranzo, bike_shop, beach, viewpoint };
}

// ULTRA: Sichtbarkeits-Toggles für die generischen Overpass-Kategorien
// (fast_food, supermarket, bakery, …). Pro Kategorie ein persistierter Bool,
// default false — sonst startet die Karte mit 9 POI-Typen zugepflastert.
// Key-Schema adt_poi_<category>.
export interface OverpassPoiToggle {
  enabled: boolean;
  set: (v: boolean) => void;
}

export function useOverpassPoiToggles(): Record<PoiCategoryId, OverpassPoiToggle> {
  // Fixe Reihenfolge (OVERPASS_EXTRA_CATEGORIES ist konstant) → stabile
  // Hook-Reihenfolge über Re-Renders, Rules-of-Hooks bleiben erfüllt.
  const result = {} as Record<PoiCategoryId, OverpassPoiToggle>;
  for (const cat of OVERPASS_EXTRA_CATEGORIES) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [enabled, set] = useBoolPreference(`adt_poi_${cat}`, false);
    result[cat] = { enabled, set };
  }
  return result;
}
